import debounce from 'lodash/debounce';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { selectError, selectSavedViewsLoading } from 'modules/messagingInsights/selectors';
import { Analytics24 } from 'ui/icons';
import { ALIGNMENT } from 'ui/menu/constants';
import { BasicFilterSelect, SelectValueDisplay } from 'ui/selects';
import { body2, body2Bold, colors, duration, easing, spacing } from 'ui/theme';
import { useViewContext } from '../../context';
import { useSavedViewsDropdownOptions } from '../SavedViews/hooks/useSavedViewsDropdownOptions';

const StyledSelectValueDisplay = styled(SelectValueDisplay)`
  width: ${spacing.x24};
`;

const StyledEmptyState = styled.div`
  ${body2Bold}
`;

const StyledLink = styled(Link)`
  ${body2}
  flex-direction: row;
  gap: ${spacing.half};
  background: none;
  color: ${colors.textLink};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: ${spacing.half} 0;
  transition: all ${duration.short} ${easing.entrance};
  > svg {
    color: ${colors.iconInteractive};
  }
  &:hover {
    background: ${colors.backgroundInteractive};
    border-radius: ${spacing.quarter};
    color: ${colors.textLink};
    text-decoration: none;
    > svg {
      color: ${colors.iconInteractiveHover};
    }
  }
  &:active {
    background: ${colors.backgroundInteractive};
    color: ${colors.textLink};
    text-decoration: none;
    > svg {
      color: ${colors.iconInteractiveHover};
    }
  }
`;

const EmptyStateContainer = styled.div`
  padding: ${spacing.half};
`;

export const SavedViewsDropdown = () => {
  const history = useHistory();
  const { options, canFetch, onFetch, onSearchChange, setIsSearching, isSearching } =
    useSavedViewsDropdownOptions();
  const { viewId } = useViewContext();
  const savedViewsLoading = useSelector(selectSavedViewsLoading);
  const [selectedView, setSelectedView] = useState(viewId ? [viewId] : []);
  const error = useSelector(selectError);
  const [previousVal, setPreviousVal] = useState('');

  // Clear dropdown selection when a view is not found/doesn't exist
  useEffect(() => {
    if (error.fetchSavedView === 404) {
      setSelectedView([]);
    }
  }, [error]);

  const choosePlaceholder = (opts: any[], loading: boolean) => {
    if (loading) {
      return 'Loading...';
    }
    if (opts?.length) {
      return 'Choose from saved views';
    }
    return 'No saved views yet';
  };

  const chooseSavedView = (selectedViewId: any[]) => {
    const id = selectedViewId[0];
    if (id) {
      setSelectedView(selectedViewId);
      const newPath = `/messagingInsights/views/${id}`;
      history.replace(newPath);
    }
  };

  const baseContent = {
    triggerPlaceholder: 'Search views',
    filterPlaceholder: 'Search views',
  };

  const handleSearchChange = (val?: string) => {
    if (val !== undefined && previousVal !== val) {
      onSearchChange(val);
      setPreviousVal(val);
    }
  };

  const debo = debounce((str) => handleSearchChange(str), 300);
  return (
    <>
      {(options?.length > 0 || previousVal !== '' || isSearching) && (
        <BasicFilterSelect
          autoFocusOnInput
          isFullWidth
          defaultValues={selectedView}
          emptyStateText={
            <EmptyStateContainer>
              <StyledEmptyState>No saved views match your search</StyledEmptyState>
              <div>Try a different search term</div>
            </EmptyStateContainer>
          }
          id={'savedViewsDropdown'}
          options={options}
          placeholder={choosePlaceholder(options, savedViewsLoading)}
          onSelect={chooseSavedView}
          canFetch={canFetch}
          onFetch={() => onFetch()}
          horizontalAlignment={ALIGNMENT.RIGHT}
          isDisabled={savedViewsLoading}
          onSearchChange={(str) => {
            setIsSearching(true);
            debo(str);
          }}
          disableInternalFiltering
          showAllCategories
          isLoading={isSearching}
          Trigger={StyledSelectValueDisplay}
          inputContent={baseContent}
          footerButtonContent={
            <StyledLink to={'/savedViews'}>
              <Analytics24 isColorInherited />
              View all saved views
            </StyledLink>
          }
        />
      )}
    </>
  );
};
